import React, { useEffect, useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { IoCalendarClearOutline } from "react-icons/io5";
import { ICarnetEmployee } from "Dashboard/scenes/Carnets/domain/ICarnetEmployee";
import { FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { useAppDispatch } from "hooks/hooks";
import DatePicker from "react-datepicker";
import {
  setSelectedIndex,
  updateCarnetsHours,
  updateCarnetsCheckbox,
  updateCarnetsProject,
  updateEmployeeData,
  setVacationPopover,
  setInfoPopover,
  updateCarnetsDate,
  setPayrollPopover,
} from "../main.slice";
import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import customStyles from "./SelectStyle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ICarnetCreate } from "Dashboard/scenes/Carnets/domain/ICarnetCreate";
import { AbsenceType } from "Dashboard/scenes/Carnets/domain/AbsenceType";
import { MealType } from "Dashboard/scenes/Carnets/domain/MealType";
import { CarnetType } from "Dashboard/scenes/Carnets/domain/CarnetType";
import InfoTooltip from "components/Popup/InfoTooltip";
import { LiaWalletSolid } from "react-icons/lia";
import { ICarnetDateUpdate } from "Dashboard/scenes/Carnets/domain/ICarnetDateUpdate";
import { IProjectOption } from "Dashboard/domain/IProjectOption";
import { useSelector } from "react-redux";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { selectState as selectStateDashboard } from "Dashboard/dashboard.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { CARNET_MODULE_NAME, CARNET_PAYROLL_MODULE_NAME } from "Dashboard/scenes/Carnets/domain/CarnetModule";
import NumberInput from "Dashboard/components/NumberInput";

interface IProps {
  carnetEmployee: ICarnetEmployee;
  index: number;
  options: IProjectOption[];
  input: ICarnetCreate | undefined;
  dateInput: ICarnetDateUpdate | undefined;
};

function getDayString(days: number): String {
  if (days > 0) {
    return `${days}`;
  }
  return "-";
};

const TableRow = ({ carnetEmployee, options, input, dateInput, index }: IProps) => {
  const dispatch = useAppDispatch();

  const [latestProjectName, setLatestProjectName] = useState("");
  const { user } = useSelector(selectStateAuth);
  const { provisions } = useSelector(selectStateDashboard);
  const availableProjectIds = options.map(option => option.value);
  const projectId =
    carnetEmployee.latestProjectId !== undefined
      && availableProjectIds.includes(carnetEmployee.latestProjectId)
      ? carnetEmployee.latestProjectId : undefined;

  const onChangeHours = (rateIndex: number, value: number) => {
    dispatch(
      updateCarnetsHours({
        employeeId: carnetEmployee.employeeId,
        projectId: projectId,
        rateIndex: rateIndex,
        value: value,
      })
    );
  };

  const onChangeCarnetDate = (date: Date | null) => {
    if (date != null) {
      dispatch(updateCarnetsDate({
        employeeId: carnetEmployee.employeeId,
        carnetDate: FormatDateIso(date)
      }));
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateCarnetsCheckbox({
        employeeId: carnetEmployee.employeeId,
        name: e.target.name,
        checked: e.target.checked,
      })
    );
  };

  const onHealthInsuranceUntilDateChange = async (date: Date | null) => {
    const dateToString = date ? FormatDateIso(date) : null;
    await dispatch(updateEmployeeData({
      employeeId: carnetEmployee.employeeId,
      healthInsuranceUntil: dateToString
    }));
  };

  const onEmployedUntilDateChange = async (date: Date | null) => {
    const dateToString = date ? FormatDateIso(date) : null;
    await dispatch(updateEmployeeData({
      employeeId: carnetEmployee.employeeId,
      employedUntil: dateToString
    }));
  };

  useEffect(() => {
    const latestProjectOptionArray = options.filter(
      projectOption =>
        projectOption.value === carnetEmployee.latestProjectId);
    if (latestProjectOptionArray.length === 1) {
      setLatestProjectName(latestProjectOptionArray[0].label);
    }
  }, [options, carnetEmployee.latestProjectId]);

  //props for carnet list modal

  const inputHoursError =
    input?.carnetType === CarnetType.EMPLOYED &&
    input.carnetHours?.every(hoursEntry => hoursEntry === undefined);

  return (
    <div
      className={`row ${hasWriteAccessTo(user, CARNET_PAYROLL_MODULE_NAME) ? 'with-payroll' : ''}`}
      style={{ "--carnet-hour-columns": provisions?.activeHourlyRateNames.length } as React.CSSProperties}
      key={index}
    >
      <div className="col center uppercase strong">
        <div className="bg-item overflowMinWidth center cell-name">
          <InfoTooltip tooltip={carnetEmployee.employeeName}
            classesProp="bg-text-overflow">
            {carnetEmployee.employeeName}
          </InfoTooltip>
        </div>
        {carnetEmployee.contractEndingSoon && (
          <div
            className="badge red enable-tooltip tt-right"
            aria-label="Istek zaposlenja..."
          >
            <FiberManualRecordIcon
              className="fas"
              fontSize="small"
              style={{ fontSize: 8 }}
            />
          </div>
        )}
      </div>
      <div className="col infoIcon center">
        <span>
          <AiOutlineInfoCircle
            color="rgb(83, 83, 83)"
            size={16}
            onClick={hasWriteAccessTo(user, CARNET_MODULE_NAME)
              ? (e) => {
                dispatch(setSelectedIndex(index));
                dispatch(setInfoPopover({
                  isOpen: true,
                  top: e.clientY,
                  left: e.clientX,
                  offsetLeft: 23,
                  offsetTop: -37,
                }))
              }
              : undefined} />
        </span>
      </div>
      {hasWriteAccessTo(user, CARNET_PAYROLL_MODULE_NAME) && (
        <div className="col carnetWatch center">
          <span>
            <LiaWalletSolid
              color="rgb(83, 83, 83)"
              size={17}
              onClick={(e) => {
                dispatch(setSelectedIndex(index));
                dispatch(
                  setPayrollPopover({
                    isOpen: true,
                    top: e.clientY,
                    left: e.clientX,
                    offsetLeft: 20,
                    offsetTop: -37,
                  })
                );
              }} />
          </span>
        </div>
      )}
      <div className="col left">
        {hasWriteAccessTo(user, CARNET_MODULE_NAME)
          ? (
            <div className="customSelect">
              <ProjectSelect
                options={options}
                onChange={(value: number) => {
                  dispatch(
                    updateCarnetsProject({
                      employeeId: carnetEmployee.employeeId,
                      projectId: value,
                    })
                  );
                }}
                value={projectId}
                customStyles={customStyles}
                error={
                  input?.carnetType === CarnetType.EMPLOYED && (projectId === null || projectId === undefined)
                }
              />
            </div>
          )
          : (
            <div className="bg-item bg-item-border-light">{latestProjectName}</div>
          )}
      </div>
      {provisions?.activeHourlyRateNames.map((rateName) => (
        <div className="col center" key={rateName.index}>
          <NumberInput
            formatAs="quantity"
            value={`${input?.carnetHours?.[rateName.index]}`}
            onChange={(
              value: string, unmaskedValue: string, parsedValue: number
            ) => onChangeHours(rateName.index, parsedValue)}
            className={`input-center white${inputHoursError ? " error" : ""}`}
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </div>
      ))}
      <div className="col center">
        {hasWriteAccessTo(user, CARNET_MODULE_NAME)
          ? (
            <div>
              <DatePicker
                popperProps={{ strategy: "fixed" }}
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date) => { onChangeCarnetDate(date) }}
                selected={dateInput?.carnetDate ? new Date(dateInput.carnetDate) : new Date()}
                className="center"
              />
            </div>
          )
          : (
            <div className="bg-item"></div>
          )}
      </div>
      <div className="col center">
      </div>
      <div className="col center">
        <InfoTooltip tooltip="Doručak" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.breakfast
                  ? input.breakfast === MealType.REGULAR
                  : false
                : false
            }
            name="BREAKFAST_REGULAR"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Ručak" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.lunch
                  ? input.lunch === MealType.REGULAR
                  : false
                : false
            }
            name="LUNCH_REGULAR"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Večera" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.dinner
                  ? input.dinner === MealType.REGULAR
                  : false
                : false
            }
            name="DINNER_REGULAR"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
      </div>
      <div className="col center">
        <InfoTooltip tooltip="Doručak" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.breakfast
                  ? input.breakfast === MealType.CASH
                  : false
                : false
            }
            name="BREAKFAST_CASH"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Ručak" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.lunch
                  ? input.lunch === MealType.CASH
                  : false
                : false
            }
            name="LUNCH_CASH"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
        <InfoTooltip tooltip="Večera" element="span">
          <input
            type="checkbox"
            onChange={onChange}
            checked={
              input
                ? input.dinner
                  ? input.dinner === MealType.CASH
                  : false
                : false
            }
            name="DINNER_CASH"
            disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
          />
        </InfoTooltip>
      </div>
      <div className="col center">
        <input
          type="checkbox"
          name="SICK_LEAVE"
          checked={input?.absenceType === AbsenceType.SICK_LEAVE}
          onChange={onChange}
          disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
        />
      </div>
      <div className="col center">
        <span
          onClick={hasWriteAccessTo(user, CARNET_MODULE_NAME)
            ? (e) => {
              dispatch(setSelectedIndex(index));
              dispatch(setVacationPopover({
                isOpen: true,
                left: e.clientX,
                top: e.clientY + 5,
              }));
            }
            : undefined}
        //disabled={!hasWriteAccessTo(user, CARNET_MODULE_NAME)}
        >
          <IoCalendarClearOutline
            color="rgb(83, 83, 83)"
            size={15} />
        </span>
      </div>
      <div className="col center light">
        {carnetEmployee.employedUntil
          ? (hasWriteAccessTo(user, CARNET_MODULE_NAME)
            ? <div>
              <DatePicker
                popperProps={{ strategy: "fixed" }}
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date) => { onEmployedUntilDateChange(date); }}
                selected={new Date(carnetEmployee.employedUntil)}
                className="healthInsDate center"
              />
            </div>
            : <div className="bg-item center">{FormatDateShort(carnetEmployee.employedUntil)}</div>)
          : (hasWriteAccessTo(user, CARNET_MODULE_NAME)
            ? <div className="customDatepicker">
              <DatePicker
                popperProps={{ strategy: "fixed" }}
                showMonthDropdown
                showYearDropdown
                customInput={<span className="datePickerSpan">-</span>}
                onChange={(date) => { onEmployedUntilDateChange(date); }}
              />
            </div>
            : <span className="datePickerSpan">-</span>)}
        {carnetEmployee.notes && (
          <div
            className="badge red enable-tooltip tt-right"
            aria-label={carnetEmployee.notes}
          ></div>
        )}
      </div>
      <div className="col center">
        {hasWriteAccessTo(user, CARNET_MODULE_NAME)
          ?
          (<div>
            <DatePicker
              popperProps={{ strategy: "fixed" }}
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date) => onHealthInsuranceUntilDateChange(date)}
              selected={carnetEmployee.healthInsuranceUntil ? new Date(carnetEmployee.healthInsuranceUntil) : null}
              isClearable={false}
              className="healthInsDate center"
            />
          </div>)
          : (
            carnetEmployee.healthInsuranceUntil
              ? <div className="bg-item center">{FormatDateShort(carnetEmployee.healthInsuranceUntil)}</div>
              : <div className="bg-item center">-</div>
          )}
      </div>
      <div className="col center text-small">
        {getDayString(carnetEmployee.vacationDaysUsed)}
      </div>
      <div className="col left light text-small">{carnetEmployee.phone}</div>
    </div>
  );
};

export default React.memo(TableRow);
